/**
 * Centralized Navigation Configuration
 * 
 * Defines the complete navigation tree for VivyaWatch admin dashboard.
 * Each section/item includes:
 *   - id: Route identifier
 *   - labelKey: i18n translation key
 *   - icon: Lucide icon name (string, resolved at render time)
 *   - roles: Allowed user roles
 *   - plans: Plans that include this feature
 *   - subgroups: Optional nested groupings (for DLP, Integrations)
 */

// ─── Role sets ────────────────────────────────────────────────────────────────
// Platform-level super admins (VivyaWatch company)
const SUPER_ONLY = ['super_admin'];

// ALL tenant-level admin roles (full dashboard access, org-wide data)
const ALL_ADMINS = ['admin', 'org_admin', 'super_admin', 'client_admin', 'super_client_admin'];

// Owner-only roles: Tenant Admin, Super Admin, Super Client Admin
// Used for Billing & Usage — client_admin (sub-admin) does NOT see billing
const TENANT_ADMIN_ONLY = ['org_admin', 'super_admin', 'super_client_admin'];

// Role management: only the tenant owner and platform admins can assign roles.
// Enterprise principle: sub-admins (client_admin/admin) cannot manage who gets access.
const ROLE_MANAGERS = ['org_admin', 'super_client_admin', 'super_admin'];

// Admin + Manager: operational/data views — backend always scopes manager to their team_ids
const ADMINS_AND_MANAGER = [...ALL_ADMINS, 'manager'];

// Plan gates
const ALL_PLANS = ['essentials', 'performance', 'secure', 'defence'];
const SECURE_UP = ['secure', 'defence'];
const PERFORMANCE_UP = ['performance', 'secure', 'defence'];
const DEFENCE_ONLY = ['defence'];

/**
 * Navigation sections - ordered array defining sidebar structure.
 * Each section is a top-level collapsible group.
 */
export const NAVIGATION_SECTIONS = [
  // 1. Overview
  {
    id: 'overview',
    labelKey: 'sidebar.overview',
    icon: 'Activity',
    plans: ALL_PLANS,
    items: [
      { id: 'overview', labelKey: 'sidebar.dashboard', icon: 'LayoutDashboard', roles: ADMINS_AND_MANAGER, plans: ALL_PLANS },
    ],
  },

  // 1b. Devices & Agents — expanded into sidebar with grouped sub-items
  {
    id: 'devicesAgents',
    labelKey: 'sidebar.devicesAgents',
    icon: 'Monitor',
    plans: ALL_PLANS,
    subgroups: [
      {
        id: 'devices-main',
        labelKey: null, // no group header — Device List stands alone
        items: [
          { id: 'devices', labelKey: 'sidebar.deviceList', icon: 'Monitor', roles: ADMINS_AND_MANAGER, plans: ALL_PLANS },
        ],
      },
      {
        id: 'devices-workforce',
        labelKey: 'sidebar.workforce',
        items: [
          { id: 'devices-employees', labelKey: 'sidebar.employees', icon: 'User', roles: ADMINS_AND_MANAGER, plans: ALL_PLANS },
          { id: 'devices-departments', labelKey: 'sidebar.departments', icon: 'Building2', roles: ADMINS_AND_MANAGER, plans: ALL_PLANS },
          { id: 'devices-teams', labelKey: 'sidebar.teams', icon: 'Users', roles: ADMINS_AND_MANAGER, plans: ALL_PLANS },
          { id: 'devices-shifts', labelKey: 'sidebar.shiftManagement', icon: 'Clock', roles: ADMINS_AND_MANAGER, plans: ALL_PLANS },
          { id: 'devices-mapping', labelKey: 'sidebar.deviceMapping', icon: 'Link2', roles: ADMINS_AND_MANAGER, plans: ALL_PLANS },
          { id: 'identity-link', labelKey: 'sidebar.identityLink', icon: 'Link', roles: ADMINS_AND_MANAGER, plans: ALL_PLANS },
        ],
      },
      {
        id: 'devices-agent',
        labelKey: 'sidebar.agent',
        items: [
          { id: 'devices-deployment', labelKey: 'sidebar.agentDeployment', icon: 'Download', roles: ADMINS_AND_MANAGER, plans: ALL_PLANS },
          { id: 'devices-builds', labelKey: 'sidebar.agentBuilds', icon: 'Package', roles: ADMINS_AND_MANAGER, plans: ALL_PLANS },
          { id: 'devices-succession', labelKey: 'sidebar.deviceSuccession', icon: 'ArrowRightLeft', roles: ADMINS_AND_MANAGER, plans: ALL_PLANS },
          { id: 'device-control', labelKey: 'sidebar.deviceControl', icon: 'Lock', roles: ADMINS_AND_MANAGER, plans: ALL_PLANS },
        ],
      },
    ],
  },

  // 2. Monitoring
  {
    id: 'monitoring',
    labelKey: 'sidebar.monitoring',
    icon: 'Eye',
    plans: ALL_PLANS,
    items: [
      { id: 'screenshots', labelKey: 'sidebar.screenshots', icon: 'Camera', roles: ADMINS_AND_MANAGER, plans: ALL_PLANS },
      { id: 'recording', labelKey: 'sidebar.screenRecording', icon: 'Video', roles: ADMINS_AND_MANAGER, plans: ALL_PLANS },
      // Live Stream: premium add-on; requires recording. Aliased to the Recording page for now (no dedicated Live View page yet).
      { id: 'live-stream', labelKey: 'sidebar.liveStream', icon: 'Radio', roles: ADMINS_AND_MANAGER, plans: ALL_PLANS, requires: ['recording'] },
      { id: 'application_monitoring', labelKey: 'sidebar.applicationMonitoring', icon: 'Monitor', roles: ADMINS_AND_MANAGER, plans: ALL_PLANS },
      { id: 'session-events', labelKey: 'sidebar.sessionEvents', icon: 'LogIn', roles: ADMINS_AND_MANAGER, plans: ALL_PLANS },
      { id: 'network-activity', labelKey: 'sidebar.networkActivity', icon: 'Wifi', roles: ADMINS_AND_MANAGER, plans: ALL_PLANS },
      { id: 'geolocation', labelKey: 'sidebar.geolocationTracking', icon: 'MapPin', roles: ADMINS_AND_MANAGER, plans: ALL_PLANS },
      { id: 'screenshot-logs', labelKey: 'sidebar.screenshotAccess', icon: 'Eye', roles: ADMINS_AND_MANAGER, plans: ALL_PLANS },
      { id: 'clipboard-monitoring', labelKey: 'sidebar.clipboardMonitoring', icon: 'Clipboard', roles: ALL_ADMINS, plans: ALL_PLANS },
      { id: 'screen-capture-detection', labelKey: 'sidebar.screenCaptureDetection', icon: 'ScanLine', roles: ALL_ADMINS, plans: ALL_PLANS },
      { id: 'throttle-skip-log', labelKey: 'sidebar.throttleSkipLog', icon: 'Gauge', roles: ALL_ADMINS, plans: ALL_PLANS },
    ],
  },

  // 3. UEBA & Risk — moved up; security insights are core to a monitoring tool
  {
    id: 'uebaRisk',
    labelKey: 'sidebar.uebaRisk',
    icon: 'ShieldAlert',
    plans: SECURE_UP,
    items: [
      { id: 'ueba', labelKey: 'sidebar.uebaBaselines', icon: 'Shield', roles: ALL_ADMINS, plans: SECURE_UP },
      { id: 'fake-activity', labelKey: 'sidebar.fakeActivityDetection', icon: 'MousePointer', roles: ALL_ADMINS, plans: SECURE_UP },
      { id: 'exfiltration-detection', labelKey: 'sidebar.exfiltrationDetection', icon: 'Upload', roles: ALL_ADMINS, plans: SECURE_UP },
      { id: 'github-activity', labelKey: 'sidebar.githubActivity', icon: 'Github', roles: ALL_ADMINS, plans: SECURE_UP },
      { id: 'sentiment-analysis', labelKey: 'sidebar.sentimentBehavioralRisk', icon: 'Smile', roles: ALL_ADMINS, plans: SECURE_UP },
    ],
  },

  // 4. Security Operations — moved up; alerts & policies need quick access in a monitoring tool
  {
    id: 'securityOperations',
    labelKey: 'sidebar.securityOperations',
    icon: 'Shield',
    plans: SECURE_UP,
    items: [
      { id: 'security-compliance', labelKey: 'sidebar.securityDashboard', icon: 'ShieldCheck', roles: ALL_ADMINS, plans: SECURE_UP },
      { id: 'alerts', labelKey: 'sidebar.alerts', icon: 'Bell', roles: ALL_ADMINS, plans: SECURE_UP },
      { id: 'policy-management', labelKey: 'sidebar.policyManagement', icon: 'Lock', roles: ALL_ADMINS, plans: SECURE_UP },
      { id: 'forensics-approvals', labelKey: 'sidebar.forensicsApprovals', icon: 'Microscope', roles: ALL_ADMINS, plans: SECURE_UP },
      { id: 'alert-rules', labelKey: 'sidebar.alertRules', icon: 'Zap', roles: ALL_ADMINS, plans: ALL_PLANS },
    ],
  },

  // 5. Productivity & Collaboration
  //    Meetings data comes from Calendar Integration (Integrations → Calendar Integration).
  //    Both modules must be enabled together in the plan — see "requires" notes below.
  {
    id: 'productivity',
    labelKey: 'sidebar.productivity',
    icon: 'TrendingUp',
    plans: ALL_PLANS,
    items: [
      { id: 'productivity', labelKey: 'sidebar.productivityOverview', icon: 'Target', roles: ADMINS_AND_MANAGER, plans: ALL_PLANS },
      // Productive Hours — moved here from Attendance & HR (team productive-time analytics)
      { id: 'productive-hours', labelKey: 'sidebar.productiveHours', icon: 'TrendingUp', roles: ALL_ADMINS, plans: PERFORMANCE_UP },
      // requires: calendar-integration (Integrations → Calendar & Meetings)
      { id: 'meetings', labelKey: 'sidebar.meetings', icon: 'Video', roles: ADMINS_AND_MANAGER, plans: ALL_PLANS, requires: ['calendar-integration'] },
      // requires: voip-integration (Integrations → VOIP Integration)
      { id: 'voip-calls', labelKey: 'sidebar.voipCalls', icon: 'Phone', roles: ADMINS_AND_MANAGER, plans: PERFORMANCE_UP, requires: ['voip-integration'] },
      { id: 'ai-analytics', labelKey: 'sidebar.aiAnalytics', icon: 'TrendingUp', roles: ADMINS_AND_MANAGER, plans: PERFORMANCE_UP },
      { id: 'reports', labelKey: 'sidebar.reportBuilder', icon: 'FileText', roles: ADMINS_AND_MANAGER, plans: ALL_PLANS },
    ],
  },

  // 6. DLP & Screen Intelligence — Admin-only
  //    Cloud & Email Intelligence sub-section requires Microsoft 365 / Google OAuth from
  //    Integrations → Identity & SSO.  Both must be enabled in the plan together.
  {
    id: 'dlpScreenIntelligence',
    labelKey: 'sidebar.dlpScreenIntelligence',
    icon: 'ShieldCheck',
    plans: SECURE_UP,
    subgroups: [
      {
        id: 'dlp-policies',
        labelKey: 'sidebar.dlpPolicies',
        hidden: true,
        items: [
          { id: 'data-classification', labelKey: 'sidebar.dataClassification', icon: 'Tags', roles: ALL_ADMINS, plans: SECURE_UP },
          { id: 'channel-rules', labelKey: 'sidebar.channelRules', icon: 'Filter', roles: ALL_ADMINS, plans: SECURE_UP },
        ],
      },
      {
        id: 'dlp-incidents',
        labelKey: 'sidebar.dlpIncidents',
        items: [
          { id: 'endpoint-dlp', labelKey: 'sidebar.endpointDlp', icon: 'Monitor', roles: ALL_ADMINS, plans: SECURE_UP },
          { id: 'print-monitoring', labelKey: 'sidebar.printMonitoring', icon: 'Printer', roles: ALL_ADMINS, plans: SECURE_UP },
          { id: 'high-risk-processes', labelKey: 'sidebar.highRiskProcesses', icon: 'Zap', roles: ALL_ADMINS, plans: SECURE_UP },
          { id: 'bluetooth-monitoring', labelKey: 'sidebar.bluetoothMonitoring', icon: 'Bluetooth', roles: ALL_ADMINS, plans: SECURE_UP },
          { id: 'wireless-sharing', labelKey: 'sidebar.wirelessSharing', icon: 'Share2', roles: ALL_ADMINS, plans: SECURE_UP },
        ],
      },
      {
        id: 'file-monitoring-group',
        labelKey: 'sidebar.fileMonitoring',
        items: [
          { id: 'file-monitoring', labelKey: 'sidebar.fileMonitoring', icon: 'FileText', roles: ALL_ADMINS, plans: ALL_PLANS },
        ],
      },
      {
        id: 'dlp-reports',
        labelKey: 'sidebar.dlpReports',
        hidden: true,
        items: [
          { id: 'dlp-overview', labelKey: 'sidebar.dlpOverview', icon: 'BarChart3', roles: ALL_ADMINS, plans: SECURE_UP },
          { id: 'dlp-top-violations', labelKey: 'sidebar.dlpTopViolations', icon: 'AlertTriangle', roles: ALL_ADMINS, plans: SECURE_UP },
          { id: 'dlp-top-users', labelKey: 'sidebar.dlpTopUsers', icon: 'Users', roles: ALL_ADMINS, plans: SECURE_UP },
          { id: 'dlp-top-channels', labelKey: 'sidebar.dlpTopChannels', icon: 'Hash', roles: ALL_ADMINS, plans: SECURE_UP },
        ],
      },
      {
        // Cloud Documents + M365 Audit + Email Analytics all require Microsoft 365 / Google OAuth
        // from Integrations → Identity & SSO.  Enable as a bundle in plan management.
        id: 'cloud-email-intelligence',
        labelKey: 'sidebar.cloudEmailIntelligence',
        items: [
          { id: 'cloud-documents', labelKey: 'sidebar.cloudDocuments', icon: 'Cloud', roles: ALL_ADMINS, plans: PERFORMANCE_UP, requires: ['identity-sso'] },
          { id: 'm365-audit', labelKey: 'sidebar.m365Audit', icon: 'ClipboardList', roles: ALL_ADMINS, plans: PERFORMANCE_UP, requires: ['identity-sso'] },
          { id: 'gworkspace-audit', labelKey: 'sidebar.gworkspaceAudit', icon: 'ShieldCheck', roles: ALL_ADMINS, plans: PERFORMANCE_UP, requires: ['identity-sso'] },
          { id: 'email-analytics', labelKey: 'sidebar.emailAnalytics', icon: 'Mail', roles: ALL_ADMINS, plans: PERFORMANCE_UP, requires: ['identity-sso'] },
        ],
      },
      {
        id: 'usb-security-group',
        labelKey: 'sidebar.usbSecurity',
        items: [
          { id: 'usb-security', labelKey: 'sidebar.usbSecurity', icon: 'Usb', roles: ALL_ADMINS, plans: SECURE_UP },
        ],
      },
      {
        id: 'web-security-group',
        labelKey: 'sidebar.webSecurityGroup',
        items: [
          { id: 'domain-blocking', labelKey: 'sidebar.domainBlocking', icon: 'Globe', roles: ALL_ADMINS, plans: SECURE_UP },
          { id: 'app-control', labelKey: 'sidebar.appControl', icon: 'AppWindow', roles: ALL_ADMINS, plans: SECURE_UP },
          // work-hours-policy hidden — not currently enforcing outside-hours restrictions
          // { id: 'work-hours-policy', labelKey: 'sidebar.workHoursPolicy', icon: 'Clock', roles: ALL_ADMINS, plans: SECURE_UP },
        ],
      },
    ],
  },

  // 7. Compliance — Admin-only (managers excluded; grant via custom role if needed)
  {
    id: 'compliance',
    labelKey: 'sidebar.compliance',
    icon: 'FileCheck',
    plans: SECURE_UP,
    items: [
      { id: 'regulatory-frameworks', labelKey: 'sidebar.regulatoryFrameworks', icon: 'Scale', roles: ALL_ADMINS, plans: SECURE_UP },
      { id: 'compliance', labelKey: 'sidebar.complianceDashboard', icon: 'Shield', roles: ALL_ADMINS, plans: SECURE_UP },
    ],
  },

  // 8. Attendance & HR — Admin-only (managers excluded; grant via custom role if needed)
  {
    id: 'attendanceHr',
    labelKey: 'sidebar.attendanceHr',
    icon: 'Clock',
    plans: PERFORMANCE_UP,
    subgroups: [
      {
        id: 'attendance-group',
        labelKey: 'sidebar.attendance',
        items: [
          { id: 'daily-attendance', labelKey: 'sidebar.dailyAttendance', icon: 'Clock', roles: ALL_ADMINS, plans: PERFORMANCE_UP },
          { id: 'monthly-attendance', labelKey: 'sidebar.monthlyAttendance', icon: 'Calendar', roles: ALL_ADMINS, plans: PERFORMANCE_UP },
          { id: 'shifts-calendar', labelKey: 'sidebar.shiftsCalendar', icon: 'CalendarOff', roles: ALL_ADMINS, plans: PERFORMANCE_UP },
          { id: 'biometric-devices', labelKey: 'sidebar.biometricDevices', icon: 'Fingerprint', roles: ALL_ADMINS, plans: PERFORMANCE_UP },
          { id: 'swipe-approvals', labelKey: 'sidebar.swipeApprovals', icon: 'ClipboardCheck', roles: ADMINS_AND_MANAGER, plans: PERFORMANCE_UP },
          { id: 'od-approvals', labelKey: 'sidebar.odApprovals', icon: 'Briefcase', roles: ADMINS_AND_MANAGER, plans: PERFORMANCE_UP },
        ],
      },
      {
        id: 'leave-timeoff-group',
        labelKey: 'sidebar.leaveTimeOff',
        items: [
          { id: 'leave-management', labelKey: 'sidebar.leaveManagement', icon: 'BookOpen', roles: ALL_ADMINS, plans: PERFORMANCE_UP },
          { id: 'manager-leave-approvals', labelKey: 'sidebar.managerLeaveApprovals', icon: 'CalendarCheck', roles: ADMINS_AND_MANAGER, plans: PERFORMANCE_UP },
        ],
      },
      {
        id: 'payroll-compliance-group',
        labelKey: 'sidebar.payrollCompliance',
        items: [
          { id: 'payroll', labelKey: 'sidebar.payroll', icon: 'Calculator', roles: ALL_ADMINS, plans: PERFORMANCE_UP },
          { id: 'documents-vault', labelKey: 'sidebar.documentsVault', icon: 'FileText', roles: ALL_ADMINS, plans: PERFORMANCE_UP },
          { id: 'staffing', labelKey: 'sidebar.staffing', icon: 'Users', roles: ALL_ADMINS, plans: PERFORMANCE_UP },
          { id: 'external-workforce', labelKey: 'sidebar.externalWorkforce', icon: 'Users', roles: ALL_ADMINS, plans: PERFORMANCE_UP },
          { id: 'final-settlement', labelKey: 'sidebar.finalSettlement', icon: 'ClipboardList', roles: ALL_ADMINS, plans: PERFORMANCE_UP },
          { id: 'statutory-filings', labelKey: 'sidebar.statutoryFilings', icon: 'Landmark', roles: ALL_ADMINS, plans: PERFORMANCE_UP },
        ],
      },
      {
        id: 'hr-operations-group',
        labelKey: 'sidebar.hrOperations',
        items: [
          { id: 'hrms-employees', labelKey: 'sidebar.hrmsEmployees', icon: 'Users', roles: ALL_ADMINS, plans: PERFORMANCE_UP },
          { id: 'org-chart', labelKey: 'sidebar.orgChart', icon: 'Network', roles: ADMINS_AND_MANAGER, plans: PERFORMANCE_UP },
          { id: 'reimbursements', labelKey: 'sidebar.reimbursements', icon: 'Receipt', roles: ALL_ADMINS, plans: PERFORMANCE_UP },
          { id: 'manager-reimbursements', labelKey: 'sidebar.managerReimbursements', icon: 'ClipboardCheck', roles: ADMINS_AND_MANAGER, plans: PERFORMANCE_UP },
          // { id: 'hrms-sync', labelKey: 'sidebar.hrmsSync', icon: 'RefreshCw', roles: ALL_ADMINS, plans: PERFORMANCE_UP }, // hidden for now
          { id: 'sync-conflicts', labelKey: 'sidebar.syncConflicts', icon: 'AlertTriangle', roles: ALL_ADMINS, plans: PERFORMANCE_UP },
          { id: 'hrms-configuration', labelKey: 'sidebar.hrmsConfiguration', icon: 'Settings', roles: ALL_ADMINS, plans: PERFORMANCE_UP },
        ],
      },
    ],
  },

  // 9. Integrations — Admin-only (managers excluded; grant via custom role if needed)
  // All sub-categories (Identity & SSO, Calendar, VOIP, Notifications) are now surfaced
  // inside the IntegrationsHub page (?tab=integrations). Deep-link redirects for the
  // four legacy tab IDs are handled in AdminDashboard via TAB_ALIASES + redirect effect.
  {
    id: 'integrations',
    labelKey: 'sidebar.integrations',
    icon: 'Link',
    plans: ALL_PLANS,
    items: [
      { id: 'integrations', labelKey: 'sidebar.integrations', icon: 'Link', roles: ALL_ADMINS, plans: ALL_PLANS },
    ],
  },

  // 11. Administration
  {
    id: 'administration',
    labelKey: 'sidebar.administration',
    icon: 'Settings',
    plans: ALL_PLANS,
    items: [
      // Settings: managers get view-only (enforcement done in SettingsPage via readOnly prop)
      { id: 'settings', labelKey: 'sidebar.settings', icon: 'Settings', roles: ADMINS_AND_MANAGER, plans: ALL_PLANS },
      // Modules: super admin platform only
      { id: 'modules', labelKey: 'sidebar.modules', icon: 'Package', roles: SUPER_ONLY, plans: ALL_PLANS },
      // Admin Activity: managers see their own actions only (enforced server-side)
      { id: 'admin_activity', labelKey: 'sidebar.adminActivity', icon: 'Activity', roles: ADMINS_AND_MANAGER, plans: ALL_PLANS },
      // Role Management: only admins (not managers) can manage roles
      { id: 'role-management', labelKey: 'sidebar.roleManagement', icon: 'Shield', roles: ROLE_MANAGERS, plans: ALL_PLANS },
      // Notification Settings: all admins can configure delivery channels
      { id: 'notification-settings', labelKey: 'sidebar.notificationSettings', icon: 'Bell', roles: ALL_ADMINS, plans: ALL_PLANS },
      // Billing: Tenant Admin, Super Admin, Super Client Admin only — client_admin excluded
      { id: 'billing', labelKey: 'sidebar.billingUsage', icon: 'CreditCard', roles: TENANT_ADMIN_ONLY, plans: ALL_PLANS },
      { id: 'support', labelKey: 'sidebar.support', icon: 'HelpCircle', roles: ADMINS_AND_MANAGER, plans: ALL_PLANS },
      // Avatar Notifications: team-specific motivational notification module (sellable)
      { id: 'avatar-notifications', labelKey: 'sidebar.avatarNotifications', icon: 'Smile', roles: ALL_ADMINS, plans: ALL_PLANS },
    ],
  },
];

/**
 * Helper: Get all items from a section (flattening subgroups if present)
 */
export function getSectionItems(section) {
  if (section.items) {
    return section.items;
  }
  if (section.subgroups) {
    return section.subgroups.filter(sg => !sg.hidden).flatMap(sg => sg.items);
  }
  return [];
}

/**
 * Helper: Get all navigation item IDs
 */
export function getAllItemIds() {
  return NAVIGATION_SECTIONS.flatMap(getSectionItems).map(item => item.id);
}

/**
 * Helper: Find which section contains a given item ID
 */
export function findSectionByItemId(itemId) {
  return NAVIGATION_SECTIONS.find(section => {
    const items = getSectionItems(section);
    return items.some(item =>
      item.id === itemId ||
      (item.children && item.children.some(c => c.id === itemId))
    );
  });
}

/**
 * Helper: Find a nav item (leaf item or section) by its raw ID and return
 * its i18n labelKey. Used to turn raw route/tab identifiers (e.g. from the
 * Activity Audit log or page-view tracking) into the same friendly label
 * shown in the sidebar, instead of a raw slug like "monthly-attendance".
 */
export function findItemById(itemId) {
  if (!itemId) return null;
  for (const section of NAVIGATION_SECTIONS) {
    if (section.id === itemId) return { id: section.id, labelKey: section.labelKey };
    const items = getSectionItems(section);
    const found = items.find(item => item.id === itemId);
    if (found) return { id: found.id, labelKey: found.labelKey };
  }
  return null;
}

// Acronyms that should stay upper-case when humanizing a raw ID
// (e.g. "hrms-configuration" -> "HRMS Configuration", not "Hrms Configuration").
const HUMANIZE_ACRONYMS = new Set([
  'HR', 'HRMS', 'IP', 'ID', 'URL', 'API', 'SSO', 'UEBA', 'DLP', 'USB',
  'VOIP', 'AI', 'MFA', 'CSV', 'PDF', 'IT',
]);

/**
 * Helper: Convert a raw kebab/snake/camelCase ID into a friendly, title-cased
 * label. Fallback for when no navigationConfig entry exists for the ID
 * (e.g. dynamic route segments, or legacy activity-log action slugs).
 */
export function humanizeSlug(id) {
  if (!id) return '';
  return String(id)
    .replace(/[-_]+/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .split(' ')
    .filter(Boolean)
    .map(word => {
      const upper = word.toUpperCase();
      return HUMANIZE_ACRONYMS.has(upper) ? upper : word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(' ');
}

